import {TableExportButtonsComponent} from "../../components/table-export-buttons/table-export-buttons.component";
import {ITableExportRequest, TableExportService} from "../../services/table-export.service";
import {Component, inject, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {MatCardModule} from '@angular/material/card';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatTabsModule} from '@angular/material/tabs';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatSelectModule} from '@angular/material/select';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatChipsModule} from '@angular/material/chips';
import {MatListModule} from '@angular/material/list';
import {MatDividerModule} from '@angular/material/divider';
import {MatGridListModule} from '@angular/material/grid-list';
import {FileIntegrityService} from '../../services/file-integrity.service';
import {IFileIntegrity} from '../../interfaces/file-integrity/IFileIntegrity';
import {IFileIntegrityCheckResult} from '../../interfaces/file-integrity/IFileIntegrityCheckResult';
import {
  IMultiApplicationFileIntegrityCheckResult
} from '../../interfaces/file-integrity/IMultiApplicationFileIntegrityCheckResult';
import {IApplication} from '../../interfaces/IApplication';
import {FileIntegrityCheckModeType} from "../../enum/FileIntegrityCheckModeType";
import {DatePipe} from '@angular/common';
import {TruncateChecksumPipe} from '../../pipes/truncate-checksum.pipe';

@Component({
  selector: 'app-file-integrity-dashboard',
  templateUrl: './file-integrity-dashboard.component.html',
  styleUrls: ['./file-integrity-dashboard.component.scss'],
  standalone: true,
  imports: [
    TableExportButtonsComponent,
    CommonModule,
    FormsModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatTabsModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatSelectModule,
    MatFormFieldModule,
    MatExpansionModule,
    MatToolbarModule,
    MatChipsModule,
    MatListModule,
    MatDividerModule,
    MatGridListModule,
    DatePipe,
    TruncateChecksumPipe,
  ]
})
export class FileIntegrityDashboardComponent implements OnInit {

  private fileIntegrityService: FileIntegrityService = inject(FileIntegrityService);

  public file_integrity_records: IFileIntegrity[] = [];
  public file_integrity_data_source = new MatTableDataSource<IFileIntegrity>([]);
  public check_result: IFileIntegrityCheckResult | null = null;
  public multi_app_check_result: IMultiApplicationFileIntegrityCheckResult | null = null;
  public applications: IApplication[] = [];
  public selected_application_id: number | null = null;
  public loading = false;
  public loading_applications = false;
  public checking = false;
  public error_message = '';
  public check_error = false;
  public last_check_attempted = false;
  public check_mode: FileIntegrityCheckModeType = FileIntegrityCheckModeType.Multi;

  // Computed properties for template binding
  public statusColor = '';
  public statusIcon = '';
  public statusText = '';
  public statusTitle = '';
  public validFilesCount = 0;
  public filteredApplicationsCount = 0;
  public totalFilesMonitored = 0;
  public filteredValidFilesCount = 0;
  public filteredMismatchesCount = 0;
  public filteredErrorsCount = 0;
  public selectedApplicationName = '';
  public allMismatches: any[] = [];
  public allErrors: any[] = [];

  // Memoized file status maps
  private fileStatusColorMap = new Map<string, string>();
  private fileStatusIconMap = new Map<string, string>();

  ngOnInit(): void {
    this.loadApplications();
    this.loadFileIntegrityRecords();
    this.checkAllApplicationsFileIntegrity();
  }

  private updateComputedProperties(): void {
    this.statusColor = this.computeStatusColor();
    this.statusIcon = this.computeStatusIcon();
    this.statusText = this.computeStatusText();
    this.statusTitle = this.computeStatusTitle();
    this.validFilesCount = this.computeValidFilesCount();
    this.filteredApplicationsCount = this.computeFilteredApplicationsCount();
    this.totalFilesMonitored = this.computeTotalFilesMonitored();
    this.filteredValidFilesCount = this.computeFilteredValidFilesCount();
    this.filteredMismatchesCount = this.computeFilteredMismatchesCount();
    this.filteredErrorsCount = this.computeFilteredErrorsCount();
    this.selectedApplicationName = this.computeSelectedApplicationName();
    this.allMismatches = this.computeAllMismatches();
    this.allErrors = this.computeAllErrors();

    // Update file status maps
    this.updateFileStatusMaps();
  }

  public loadApplications(): void {
    this.loading_applications = true;
    this.fileIntegrityService.getActiveApplications().subscribe({
      next: (applications) => {
        // Sort applications alphabetically by Name
        this.applications = applications.sort((a, b) => a.Name.localeCompare(b.Name));
        this.loading_applications = false;
        this.updateComputedProperties();
      },
      error: (error) => {
        console.error('Error loading applications:', error);
        this.loading_applications = false;
      }
    });
  }

  public loadFileIntegrityRecords(): void {
    this.loading = true;
    this.error_message = '';

    const request = this.selected_application_id
      ? this.fileIntegrityService.getFileIntegrityRecordsByApplication(this.selected_application_id)
      : this.fileIntegrityService.getFileIntegrityRecords();

    request.subscribe({
      next: (records) => {
        // Sort records alphabetically by Name
        const sorted_records = records.sort((a, b) => a.Name.localeCompare(b.Name));
        this.file_integrity_records = sorted_records;
        this.file_integrity_data_source.data = sorted_records;
        this.loading = false;
        this.updateComputedProperties();
      },
      error: (error) => {
        this.error_message = 'Failed to load file integrity records';
        this.loading = false;
        console.error('Error loading file integrity records:', error);
        this.updateComputedProperties();
      }
    });
  }

  public onApplicationChange(): void {
    if (this.check_mode === FileIntegrityCheckModeType.Multi && this.multi_app_check_result) {
      // If we have multi-app results, filter those instead of reloading
      this.updateFileListFromMultiAppResults();
    } else {
      // Otherwise load from database
      this.loadFileIntegrityRecords();
    }
  }

  private updateFileListFromMultiAppResults(): void {
    if (!this.multi_app_check_result) {
      return;
    }

    // Load all file records from the database, then we'll merge with check results
    this.loading = true;
    const request = this.selected_application_id
      ? this.fileIntegrityService.getFileIntegrityRecordsByApplication(this.selected_application_id)
      : this.fileIntegrityService.getFileIntegrityRecords();

    request.subscribe({
      next: (records) => {
        // Sort records alphabetically by Name
        const sorted_records = records.sort((a, b) => a.Name.localeCompare(b.Name));
        this.file_integrity_records = sorted_records;
        this.file_integrity_data_source.data = sorted_records;
        this.loading = false;
        this.updateComputedProperties();
      },
      error: (error) => {
        console.error('Error loading file integrity records:', error);
        this.loading = false;
        this.updateComputedProperties();
      }
    });
  }


  public checkFileIntegrity(): void {
    if (this.selected_application_id) {
      this.checkFileIntegrityByApplication(this.selected_application_id);
    } else {
      this.checkApiFileIntegrity();
    }
  }

  public checkApiFileIntegrity(): void {
    this.checking = true;
    this.error_message = '';
    this.check_error = false;
    this.last_check_attempted = true;
    this.check_mode = FileIntegrityCheckModeType.Single

    this.fileIntegrityService.checkFileIntegrity().subscribe({
      next: (result) => {
        this.check_result = result;
        this.multi_app_check_result = null;
        this.checking = false;
        this.check_error = false;
        this.updateComputedProperties();
      },
      error: (error) => {
        this.error_message = 'Failed to check file integrity';
        this.checking = false;
        this.check_error = true;
        this.check_result = null;
        console.error('Error checking file integrity:', error);
        this.updateComputedProperties();
      }
    });
  }

  public checkFileIntegrityByApplication(applicationTypeId: number): void {
    this.checking = true;
    this.error_message = '';
    this.check_error = false;
    this.last_check_attempted = true;
    this.check_mode = FileIntegrityCheckModeType.Single;

    this.fileIntegrityService.checkFileIntegrityByApplication(applicationTypeId).subscribe({
      next: (result) => {
        this.check_result = result;
        this.multi_app_check_result = null;
        this.checking = false;
        this.check_error = false;
        this.updateComputedProperties();
      },
      error: (error) => {
        this.error_message = 'Failed to check file integrity for application';
        this.checking = false;
        this.check_error = true;
        this.check_result = null;
        console.error('Error checking file integrity for application:', error);
        this.updateComputedProperties();
      }
    });
  }

  public checkAllApplicationsFileIntegrity(): void {
    this.checking = true;
    this.error_message = '';
    this.check_error = false;
    this.last_check_attempted = true;
    this.check_mode = FileIntegrityCheckModeType.Multi;

    this.fileIntegrityService.checkAllApplicationsFileIntegrity().subscribe({
      next: (result) => {
        this.multi_app_check_result = result;
        this.check_result = null;
        this.checking = false;
        this.check_error = false;
        this.updateFileListFromMultiAppResults();
      },
      error: (error) => {
        this.error_message = 'Failed to check file integrity for all applications';
        this.checking = false;
        this.check_error = true;
        this.multi_app_check_result = null;
        console.error('Error checking file integrity for all applications:', error);
        this.updateComputedProperties();
      }
    });
  }

  public retryCheck(): void {
    if (this.check_mode === FileIntegrityCheckModeType.Multi) {
      this.checkAllApplicationsFileIntegrity();
    } else {
      this.checkFileIntegrity();
    }
  }

  private getOverallStatus(): 'success' | 'warning' | 'error' | 'unknown' {
    if (this.check_error) {
      return 'unknown';
    }

    if (this.check_mode === FileIntegrityCheckModeType.Multi && this.multi_app_check_result) {
      if (this.multi_app_check_result.TotalErrors > 0) {
        return 'error';
      }
      if (this.multi_app_check_result.TotalMismatches > 0) {
        return 'warning';
      }
      return 'success';
    }

    if (this.check_mode === FileIntegrityCheckModeType.Single && this.check_result) {
      if (this.check_result.Errors.length > 0) {
        return 'error';
      }
      if (this.check_result.Mismatches.length > 0) {
        return 'warning';
      }
      return 'success';
    }

    return 'unknown';
  }

  private computeStatusIcon(): string {
    const status = this.getOverallStatus();
    switch (status) {
      case 'success':
        return 'check_circle';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      case 'unknown':
        return 'help';
      default:
        return 'help';
    }
  }

  private computeStatusColor(): string {
    const status = this.getOverallStatus();
    switch (status) {
      case 'success':
        return 'green';
      case 'warning':
        return 'orange';
      case 'error':
        return 'red';
      case 'unknown':
        return 'gray';
      default:
        return 'gray';
    }
  }

  private computeStatusText(): string {
    const status = this.getOverallStatus();

    if (this.check_mode === FileIntegrityCheckModeType.Multi && this.multi_app_check_result) {
      // Get filtered counts for the selected application
      const mismatches_count = this.computeFilteredMismatchesCount();
      const errors_count = this.computeFilteredErrorsCount();

      switch (status) {
        case 'success':
          // If specific application is selected, don't mention "across X applications"
          if (this.selected_application_id) {
            return 'All files are valid';
          }
          return `All files are valid across ${this.multi_app_check_result.TotalApplications} applications`;
        case 'warning':
          return `${mismatches_count} file mismatches found`;
        case 'error':
          return `${errors_count} file errors found`;
        case 'unknown':
          return 'Status unknown - Check failed';
        default:
          return 'Status unknown';
      }
    }

    switch (status) {
      case 'success':
        return 'All files are valid';
      case 'warning':
        return 'Some files have mismatches';
      case 'error':
        return 'Some files have errors';
      case 'unknown':
        return 'Status unknown - Check failed';
      default:
        return 'Status unknown';
    }
  }

  private computeValidFilesCount(): number {
    if (this.check_mode === FileIntegrityCheckModeType.Multi && this.multi_app_check_result) {
      return this.multi_app_check_result.TotalFilesChecked -
             this.multi_app_check_result.TotalMismatches -
             this.multi_app_check_result.TotalErrors;
    }

    if (this.check_result) {
      return this.check_result.TotalFilesChecked -
             this.check_result.Mismatches.length -
             this.check_result.Errors.length;
    }

    return 0;
  }

  // Public map getters for file status (used in template with direct map access)
  public get fileStatusColors(): Map<string, string> {
    return this.fileStatusColorMap;
  }

  public get fileStatusIcons(): Map<string, string> {
    return this.fileStatusIconMap;
  }

  private updateFileStatusMaps(): void {
    this.fileStatusColorMap.clear();
    this.fileStatusIconMap.clear();

    for (const file of this.file_integrity_records) {
      let has_error = false;
      let has_mismatch = false;

      // Check in single app result
      if (this.check_result) {
        has_error = this.check_result.Errors.some(error => error.Id === file.Id);
        has_mismatch = this.check_result.Mismatches.some(mismatch => mismatch.Id === file.Id);
      }

      // Check in multi-app results
      if (this.multi_app_check_result && !has_error && !has_mismatch) {
        for (const appResult of this.multi_app_check_result.ApplicationTypeResults) {
          // Only check results from the file's application
          if (parseInt(appResult.ApplicationTypeId) !== file.ApplicationTypeValue) {
            continue;
          }

          has_error = appResult.Errors.some(error => error.Id === file.Id);
          if (has_error) break;

          has_mismatch = appResult.Mismatches.some(mismatch => mismatch.Id === file.Id);
          if (has_mismatch) break;
        }
      }

      // Set color
      if (has_error) {
        this.fileStatusColorMap.set(file.Id, 'red');
      } else if (has_mismatch) {
        this.fileStatusColorMap.set(file.Id, 'orange');
      } else if (this.check_result || this.multi_app_check_result) {
        this.fileStatusColorMap.set(file.Id, 'green');
      } else {
        this.fileStatusColorMap.set(file.Id, 'gray');
      }

      // Set icon
      if (has_error) {
        this.fileStatusIconMap.set(file.Id, 'error');
      } else if (has_mismatch) {
        this.fileStatusIconMap.set(file.Id, 'warning');
      } else if (this.check_result || this.multi_app_check_result) {
        this.fileStatusIconMap.set(file.Id, 'check_circle');
      } else {
        this.fileStatusIconMap.set(file.Id, 'help');
      }
    }
  }

  private computeSelectedApplicationName(): string {
    if (!this.selected_application_id) {
      return '';
    }
    const app = this.applications.find(a => a.Value === this.selected_application_id);
    return app ? app.Name : '';
  }

  private computeStatusTitle(): string {
    if (this.selected_application_id) {
      const app_name = this.selectedApplicationName;
      return app_name ? `${app_name} Status` : 'Application Status';
    }
    return 'System Status';
  }

  private computeFilteredApplicationsCount(): number {
    if (this.selected_application_id) {
      return 1;
    }
    if (this.check_mode === FileIntegrityCheckModeType.Multi && this.multi_app_check_result) {
      return this.multi_app_check_result.TotalApplications;
    }
    return this.applications.length;
  }

  private computeFilteredValidFilesCount(): number {
    if (this.selected_application_id && this.check_mode === FileIntegrityCheckModeType.Multi && this.multi_app_check_result) {
      const appResult = this.multi_app_check_result.ApplicationTypeResults.find(
        ar => parseInt(ar.ApplicationTypeId) === this.selected_application_id
      );
      if (appResult) {
        return appResult.TotalFilesChecked - appResult.Mismatches.length - appResult.Errors.length;
      }
      return 0;
    }
    return this.validFilesCount;
  }

  private computeFilteredMismatchesCount(): number {
    if (this.selected_application_id && this.check_mode === FileIntegrityCheckModeType.Multi && this.multi_app_check_result) {
      const appResult = this.multi_app_check_result.ApplicationTypeResults.find(
        ar => parseInt(ar.ApplicationTypeId) === this.selected_application_id
      );
      return appResult ? appResult.Mismatches.length : 0;
    }
    if (this.check_mode === FileIntegrityCheckModeType.Multi && this.multi_app_check_result) {
      return this.multi_app_check_result.TotalMismatches;
    }
    if (this.check_result) {
      return this.check_result.Mismatches.length;
    }
    return 0;
  }

  private computeFilteredErrorsCount(): number {
    if (this.selected_application_id && this.check_mode === FileIntegrityCheckModeType.Multi && this.multi_app_check_result) {
      const appResult = this.multi_app_check_result.ApplicationTypeResults.find(
        ar => parseInt(ar.ApplicationTypeId) === this.selected_application_id
      );
      return appResult ? appResult.Errors.length : 0;
    }
    if (this.check_mode === FileIntegrityCheckModeType.Multi && this.multi_app_check_result) {
      return this.multi_app_check_result.TotalErrors;
    }
    if (this.check_result) {
      return this.check_result.Errors.length;
    }
    return 0;
  }

  private computeAllMismatches(): any[] {
    let mismatches: any[] = [];

    if (this.check_mode === FileIntegrityCheckModeType.Single && this.check_result) {
      mismatches = [...this.check_result.Mismatches];
    } else if (this.check_mode === FileIntegrityCheckModeType.Multi && this.multi_app_check_result) {
      for (const appResult of this.multi_app_check_result.ApplicationTypeResults) {
        // Filter by selected application if one is selected
        if (this.selected_application_id !== null && parseInt(appResult.ApplicationTypeId) !== this.selected_application_id) {
          continue;
        }
        mismatches.push(...appResult.Mismatches);
      }
    }

    // Sort alphabetically by Name
    return mismatches.sort((a, b) => a.Name.localeCompare(b.Name));
  }

  private computeAllErrors(): any[] {
    let errors: any[] = [];

    if (this.check_mode === FileIntegrityCheckModeType.Single && this.check_result) {
      errors = [...this.check_result.Errors];
    } else if (this.check_mode === FileIntegrityCheckModeType.Multi && this.multi_app_check_result) {
      for (const appResult of this.multi_app_check_result.ApplicationTypeResults) {
        // Filter by selected application if one is selected
        if (this.selected_application_id !== null && parseInt(appResult.ApplicationTypeId) !== this.selected_application_id) {
          continue;
        }
        errors.push(...appResult.Errors);
      }
    }

    // Sort alphabetically by Name
    return errors.sort((a, b) => a.Name.localeCompare(b.Name));
  }

  private computeTotalFilesMonitored(): number {
    // If we have check results, use those counts as they're more accurate
    if (this.check_mode === FileIntegrityCheckModeType.Multi && this.multi_app_check_result) {
      // If filtering by application, count only files from that application
      if (this.selected_application_id !== null) {
        const appResult = this.multi_app_check_result.ApplicationTypeResults.find(
          ar => parseInt(ar.ApplicationTypeId) === this.selected_application_id
        );
        return appResult ? appResult.TotalFilesChecked : 0;
      }
      // Otherwise return total across all applications
      return this.multi_app_check_result.TotalFilesChecked;
    }

    if (this.check_mode === FileIntegrityCheckModeType.Single && this.check_result) {
      return this.check_result.TotalFilesChecked;
    }

    // Fallback to loaded records count
    return this.file_integrity_records.length;
  }

  protected readonly FileIntegrityCheckModeType = FileIntegrityCheckModeType;

  private tableExportService: TableExportService = inject(TableExportService);

  /**
   * The checksum register as shown, full checksums rather than the truncated form on screen: the reason to
   * take this away from the dashboard is to compare it against another environment or an earlier copy, and a
   * truncated checksum cannot be compared. Status is exported as a word — the screen shows it as a coloured
   * icon, which exports as nothing at all.
   */
  private buildExportRequest(): ITableExportRequest<IFileIntegrity> {
    const rows = this.file_integrity_data_source.data;

    return {
      title: 'File integrity register',
      subtitle: this.selectedApplicationName,
      summary: [{label: 'Files', value: `${rows.length}`}],
      rows: rows,
      columns: [
        {header: 'File name', value: fileP => fileP.Name ?? ''},
        {header: 'File path', value: fileP => fileP.FilePath ?? ''},
        {header: 'Checksum', value: fileP => fileP.CurrentChecksum ?? ''},
        {header: 'Last updated', value: fileP => fileP.UpdatedOn ? new Date(fileP.UpdatedOn).toLocaleString() : ''},
        {header: 'Status', value: fileP => this.fileStatusIcons.get(fileP.Id) ?? 'unknown'}
      ],
      fileName: 'file-integrity-register'
    };
  }

  onExportRegisterCsv(): void {
    this.tableExportService.exportCsv(this.buildExportRequest());
  }

  onExportRegisterPdf(): void {
    this.tableExportService.exportPdf(this.buildExportRequest());
  }
}
